import React, { useEffect, useMemo, useState } from "react";
import { EyeOutlined, FileOutlined } from "@ant-design/icons";
import { Flex, Typography } from "antd";
import CustomModal from "../CustomModal/CustomModal";
import CustomPdfViewer from "../CustomPdfViewer/CustomPdfViewer";
import "./ViewOnlyDocument.scss";

const getFileExtension = (fileName = "", fileUrl = "") => {
  const source = String(fileName || fileUrl || "").toLowerCase();
  const match = source.match(/\.([a-z0-9]+)(?:\?|#|$)/);
  return match?.[1] || "";
};

const preventInteraction = (event) => {
  event.preventDefault();
};

const ViewOnlyDocumentBlock = ({ fileName = "Document", fileUrl = "" }) => {
  const [open, setOpen] = useState(false);
  const extension = useMemo(
    () => getFileExtension(fileName, fileUrl),
    [fileName, fileUrl],
  );
  const isPdf = extension === "pdf";
  const isWord = extension === "doc" || extension === "docx";
  const hasPreviewUrl = Boolean(String(fileUrl || "").trim());

  useEffect(() => {
    if (!open) return undefined;

    const blockRestrictedKeys = (event) => {
      const key = String(event.key || "").toLowerCase();
      const isModifier = event.ctrlKey || event.metaKey;

      if (isModifier && ["s", "p", "c", "a", "u"].includes(key)) {
        event.preventDefault();
      }

      if (key === "printscreen" || key === "f12") {
        event.preventDefault();
      }
    };

    window.addEventListener("keydown", blockRestrictedKeys, true);
    return () => window.removeEventListener("keydown", blockRestrictedKeys, true);
  }, [open]);

  const handleOpen = () => {
    if (!hasPreviewUrl) return;
    setOpen(true);
  };

  const renderPreview = () => {
    if (!hasPreviewUrl) {
      return (
        <Typography className="view-only-document-empty">
          Document preview is not available.
        </Typography>
      );
    }

    if (isPdf) {
      return <CustomPdfViewer pdfPath={fileUrl} />;
    }

    if (isWord) {
      const embeddedUrl = `https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`;
      return (
        <iframe
          src={embeddedUrl}
          title={fileName}
          className="view-only-document-iframe"
          sandbox="allow-scripts allow-same-origin"
        />
      );
    }

    return (
      <Typography className="view-only-document-empty">
        Preview is not available for this file type.
      </Typography>
    );
  };

  return (
    <>
      <div
        className={`view-only-document-block${hasPreviewUrl ? " is-clickable" : ""}`}
        onClick={handleOpen}
        onKeyDown={(event) => {
          if (hasPreviewUrl && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            handleOpen();
          }
        }}
        role={hasPreviewUrl ? "button" : undefined}
        tabIndex={hasPreviewUrl ? 0 : undefined}
      >
        <Flex align="center" gap={10} className="view-only-document-header">
          <FileOutlined className="view-only-document-icon" />
          <Typography className="view-only-document-name">{fileName}</Typography>
          <span className="view-only-document-badge">View only</span>
          {hasPreviewUrl ? (
            <button
              type="button"
              className="view-only-document-view-btn"
              onClick={(event) => {
                event.stopPropagation();
                handleOpen();
              }}
            >
              <EyeOutlined />
              View
            </button>
          ) : null}
        </Flex>
      </div>

      <CustomModal
        open={open}
        onCancel={() => setOpen(false)}
        title={fileName}
        width={920}
        maskClosable={false}
        centered
        content={(
          <div
            className="view-only-document-modal-content"
            onContextMenu={preventInteraction}
            onCopy={preventInteraction}
            onCut={preventInteraction}
            onDragStart={preventInteraction}
          >
            <div className="view-only-document-watermark" aria-hidden="true">
              <span>KARNCY</span>
              <span>VIEW ONLY</span>
              <span>CONFIDENTIAL</span>
            </div>
            <div className="view-only-document-preview-shell view-only-document-preview-shell--modal">
              {renderPreview()}
            </div>
            <Typography className="view-only-document-note">
              For viewing purposes only. Download, save, print, and screenshots are restricted.
            </Typography>
          </div>
        )}
      />
    </>
  );
};

export default ViewOnlyDocumentBlock;
